/**
 * PF Revamp — entry-point navigation hook.
 *
 * Centralizes the A/B flag + role decision tree used by every place that
 * sends a user into the purchase flow:
 *   - TopBar "New Order" split button (desktop)
 *   - TopBarDropdownMobileInternalContent (admin mobile menu)
 *   - TopBarDropdownMobileClientContent (client mobile menu)
 *   - ContinueOrderDialog (start-fresh + draft-resume nav)
 *
 * Returns the resolved landing path + helpers. Callers own drafts-dialog
 * state because that UI is TopBar-local.
 */
import { useCallback } from 'react'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Path } from 'constants/router'
import { useAuth0 } from 'utils/auth'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'

interface ResolveOptions {
  /**
   * When true, short-circuits to the catalogue (a property has already been
   * selected — the draft-resume case). Ignored for an admin with no resolved
   * target client: they must pass the property drawer's client stage first,
   * otherwise no v2 session can be opened for the order.
   */
  hasProperty?: boolean
}

export interface PfEntryNavigation {
  /** True when the PF revamp master flag is on. */
  isEnabled: boolean
  /** Resolved path for *this* user (respects role + A/B flags). */
  entryPath: string
  /** Legacy purchase flow path — used when the revamp flag is off. */
  legacyPath: string
  /** Recompute the entry path with per-call overrides. */
  resolveEntryPath: (opts?: ResolveOptions) => string
}

/**
 * Resolves the purchase-flow entry path for the current user.
 *
 * Decision tree:
 *   - PF revamp OFF → legacy `Path.PURCHASE_FLOW`.
 *   - Admin with no target client resolved → `/select-property`, where the
 *     drawer opens on its client-selection stage (the old `/select-user` page
 *     is gone; the stage lives inside `PropertySelectionDrawer`).
 *   - Property already picked → `/catalogue` (draft-resume short-circuit).
 *   - Else → `/select-property` (property-first is the locked entry path).
 */
export function usePfEntryNavigation(): PfEntryNavigation {
  const { roles } = useAuth0()

  const allowPfRevamp = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP)

  // Admin-on-behalf target. Both halves matter: the workspace scopes every
  // property query, the email is required by the v2 create-session body.
  // Subscribed for the re-render only — the decision below reads the store
  // directly, so consumers of the eager `entryPath` still refresh when the
  // target changes without the callback capturing a value that can go stale.
  usePfStore((s) => s.targetWorkspaceId)
  usePfStore((s) => s.targetUserEmail)

  const resolveEntryPath = useCallback((opts: ResolveOptions = {}): string => {
    if (!allowPfRevamp) return Path.PURCHASE_FLOW

    // Read the target through `getState()`, NOT the subscribed values above:
    // `ContinueOrderDialog.handleResumeDraft` awaits the hydration and then calls
    // this callback in the same tick, so the closure still holds the
    // pre-hydration target and an admin resuming a draft that HAS a client was
    // sent to the client stage anyway — re-asking for the client and workspace
    // the draft had just restored. `getState()` is synchronous and current.
    const { targetWorkspaceId, targetUserEmail } = usePfStore.getState()
    const hasTargetNow = !!targetWorkspaceId && !!targetUserEmail

    // Admin must resolve the target client first — the drawer's client stage
    // does that, so send them to the property entry (never straight to the
    // catalogue, which would strand them without a session).
    if (roles.isAdmin && !hasTargetNow) {
      return Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY
    }

    // Draft-resume short-circuit: if a property is already picked, skip the
    // property picker and go straight to the catalogue.
    if (opts.hasProperty) return Path.PURCHASE_FLOW_REVAMP_CATALOGUE

    // Property-first is the only entry path (catalogue-first dropped).
    return Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY
  }, [roles.isAdmin, allowPfRevamp])

  return {
    isEnabled: allowPfRevamp,
    entryPath: resolveEntryPath(),
    legacyPath: Path.PURCHASE_FLOW,
    resolveEntryPath,
  }
}
